import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Funnel, UploadSimple, DownloadSimple } from "@phosphor-icons/react";
import { useApi, money, fmtDate, errText } from "@/lib/api";
import { IntegrationCard } from "@/components/integrations/IntegrationCard";
import { useCanEdit, VIEW_ONLY_TIP } from "@/components/app/TrialBanner";
import { PipelineDialog } from "@/components/integrations/PipelineDialog";

const MAX_BYTES = 2_000_000;

const downloadTemplate = () => {
  const d = (m) => { const t = new Date(); t.setMonth(t.getMonth() + m, 15); return t.toISOString().slice(0, 10); };
  const csv = ["Deal name,Amount,Stage,Probability,Close date",
    `Acme Corp annual plan,24000,Proposal,40%,${d(1)}`, `Globex pilot,8000,Demo,20%,${d(2)}`,
    `Initech expansion,15000,Negotiation,70%,${d(3)}`].join("\n");
  const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(new Blob([csv], { type: "text/csv" })), download: "calibrunway-pipeline-template.csv" });
  a.click();
  URL.revokeObjectURL(a.href);
};

export const usePipeline = () => {
  const api = useApi();
  return useQuery({ queryKey: ["pipeline"], queryFn: () => api.get("/pipeline") });
};

export const PipelineCard = () => {
  const api = useApi();
  const qc = useQueryClient();
  const file = useRef(null);
  const [busy, setBusy] = useState(false);
  const canEdit = useCanEdit();
  const { data } = usePipeline();
  const done = (v) => { qc.setQueryData(["pipeline"], v); ["dashboard", "compare"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > MAX_BYTES) return toast.error("That file is over 2 MB. Export only open deals and try again.");
    setBusy(true);
    try {
      const v = await api.post("/pipeline/upload", { filename: f.name.slice(0, 200), csv: await f.text() });
      done(v);
      const moved = v.changes?.moved?.length;
      toast.success(`Imported ${v.summary.deals} open deals` + (v.skipped ? ` (${v.skipped} rows skipped)` : ""),
        moved ? { description: `${moved} ${moved === 1 ? "deal" : "deals"} moved their close date. See the dashboard for details.` } : undefined);
    } catch (err) { toast.error(errText(err, "Couldn't read that file")); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    try { done(await api.delete("/pipeline")); toast("Pipeline removed"); }
    catch (e) { toast.error(errText(e, "Couldn't remove the pipeline")); }
  };
  const s = data?.summary;

  return (
    <IntegrationCard testid="integration-pipeline" icon={Funnel} color="#FF7AC6" name="Sales pipeline (CSV)" status={data?.connected ? "CONNECTED" : "NOT ADDED"}
      desc={data?.connected ? `From ${data.filename}, uploaded ${fmtDate(data.uploaded_at)}. Shown as a separate "incl. pipeline" line; your main runway is unchanged.`
        : "Export open deals from any sales tool (amount, stage, chance, close date) to see runway including likely deals. Won and lost deals are ignored so nothing is counted twice."}>
      {s && (
        <div data-testid="pipeline-summary" className="mb-4 grid grid-cols-2 gap-2 font-mono text-xs">
          <div className="rounded-lg border border-[#2A2A2A] p-2"><p className="text-[#6B6B6B]">OPEN DEALS</p><p className="text-sm mt-1">{s.deals} · {money(s.total, true)}</p></div>
          <div className="rounded-lg border border-[#2A2A2A] p-2"><p className="text-[#6B6B6B]">LIKELY (WEIGHTED)</p><p data-testid="pipeline-weighted" className="text-sm mt-1 text-[#FF9FD6]">{money(s.weighted, true)}</p></div>
        </div>
      )}
      <input ref={file} type="file" accept=".csv,text/csv" className="hidden" onChange={upload} data-testid="pipeline-file-input" />
      <div className="space-y-2">
        <button data-testid="pipeline-upload-btn" disabled={busy || !canEdit} title={canEdit ? undefined : VIEW_ONLY_TIP} onClick={() => file.current?.click()}
          className="w-full flex items-center justify-center gap-2 rounded-full bg-[#FF7AC6] py-2.5 text-sm font-semibold text-[#050505] hover:shadow-[0_0_20px_rgba(255,122,198,0.5)] transition-shadow disabled:opacity-50">
          <UploadSimple size={14} /> {busy ? "Importing…" : data?.connected ? "Upload new CSV" : "Upload pipeline CSV"}
        </button>
        {data?.connected ? (
          <div className="flex gap-2">
            <PipelineDialog data={data} onSaved={done} />
            <button data-testid="pipeline-remove-btn" onClick={remove} disabled={!canEdit} title={canEdit ? undefined : VIEW_ONLY_TIP} className="flex-1 disabled:opacity-50 disabled:cursor-not-allowed rounded-full border border-[#2A2A2A] py-2 text-xs hover:border-[#FF5C7A] hover:text-[#FF5C7A] transition-colors">Remove</button>
          </div>
        ) : (
          <button data-testid="pipeline-template-btn" onClick={downloadTemplate} className="w-full flex items-center justify-center gap-2 rounded-full border border-[#2A2A2A] py-2 text-xs text-[#A3A3A3] hover:border-white hover:text-white transition-colors">
            <DownloadSimple size={13} /> Download CSV template
          </button>
        )}
      </div>
    </IntegrationCard>
  );
};
